import { lazySelector } from '@sg-core/resources/assets/js/utils';
import { isTouchDevice } from '@sg-core/resources/assets/js/utils/media';

import { audioController } from './audio-controller';

import type Plyr from 'plyr';

export class MediaPlayer {
  protected element: HTMLElement;
  protected mediaElement: HTMLMediaElement;
  protected audioController = audioController;
  protected handleVolumeListener: () => void;
  private volumeElement?: HTMLElement;
  private volumeToggle?: HTMLElement;
  private volumePopup?: HTMLElement | null;
  private updateVolumeListener: () => void;
  private toggleVolumeListener: () => void;
  private openVolumePopupListener: () => void;
  private closeVolumePopupListener: () => void;
  private focusInVolumeListener: () => void;
  private focusOutVolumeListener: () => void;

  constructor(el: string | Element, selector: string) {
    this.element = lazySelector(el);
    this.mediaElement = this.element.querySelector(selector) as HTMLMediaElement;

    this.handleVolumeListener = this.handleVolume.bind(this);
    this.updateVolumeListener = this.updateVolume.bind(this);
    this.toggleVolumeListener = this.toggleVolume.bind(this);
    this.openVolumePopupListener = this.openVolumePopup.bind(this);
    this.closeVolumePopupListener = this.closeVolumePopup.bind(this);
    this.focusInVolumeListener = this.focusInVolume.bind(this);
    this.focusOutVolumeListener = this.focusOutVolume.bind(this);
  }

  protected handleVolume() {
    this.volumeElement = this.element.querySelector('.plyr__volume') as HTMLElement;

    if (!this.volumeElement) {
      return;
    }

    this.volumeToggle = this.volumeElement.querySelector('[aria-haspopup]') as HTMLElement;

    if (this.volumeToggle) {
      const controls = this.volumeToggle.getAttribute('aria-controls');

      if (controls) {
        this.volumePopup = this.element.querySelector(`#${controls}`);
      }

      this.mediaElement.addEventListener('volumechange', this.updateVolumeListener);
      this.volumeToggle.addEventListener('click', this.toggleVolumeListener);

      if (!isTouchDevice()) {
        this.volumeElement.addEventListener('mouseover', this.openVolumePopupListener);
        this.volumeElement.addEventListener('mouseout', this.closeVolumePopupListener);
        this.volumeElement.addEventListener('focusin', this.focusInVolumeListener);
        this.volumeElement.addEventListener('focusout', this.focusOutVolumeListener);
      }

      this.updateVolume();
    }
  }

  private updateVolume() {
    if (this.mediaElement.volume === 0 || this.mediaElement.muted) {
      this.volumeElement?.classList.add('plyr__volume--muted');
    } else {
      this.volumeElement?.classList.remove('plyr__volume--muted');
    }
  }

  private toggleVolume() {
    this.mediaElement.muted = !this.mediaElement.muted;
    this.updateVolume();
  }

  private openVolumePopup() {
    if (this.volumePopup && !this.volumePopup.classList.contains('plyr--is-active')) {
      this.volumePopup.classList.add('plyr--is-active');
      this.volumeToggle?.setAttribute('aria-expanded', 'true');

      this.mediaElement.addEventListener('controlshidden', this.closeVolumePopupListener);
    }
  }

  private closeVolumePopup() {
    if (this.volumePopup && this.volumePopup.classList.contains('plyr--is-active')) {
      this.volumePopup.classList.remove('plyr--is-active');
      this.volumeToggle?.setAttribute('aria-expanded', 'false');

      this.mediaElement.removeEventListener('controlshidden', this.closeVolumePopupListener);
    }
  }

  private focusInVolume() {
    this.openVolumePopup();
  }

  private focusOutVolume() {
    requestAnimationFrame(() => {
      if (this.volumeElement && !this.volumeElement.contains(document.activeElement)) {
        this.closeVolumePopup();
      }
    });
  }

  protected getPlyrInstance(): Plyr | undefined {
    return (this.mediaElement as HTMLMediaElement & { plyr?: Plyr }).plyr;
  }

  get muted(): boolean {
    return this.mediaElement.muted;
  }

  get volume(): number {
    return this.mediaElement.volume;
  }

  destroy() {
    this.getPlyrInstance()?.destroy?.();

    if (this.volumeToggle) {
      this.mediaElement.removeEventListener('volumechange', this.updateVolumeListener);
      this.volumeToggle.removeEventListener('click', this.toggleVolumeListener);

      if (!isTouchDevice()) {
        this.volumeElement?.removeEventListener('mouseover', this.openVolumePopupListener);
        this.volumeElement?.removeEventListener('mouseout', this.closeVolumePopupListener);
        this.volumeElement?.removeEventListener('focusin', this.focusInVolumeListener);
        this.volumeElement?.removeEventListener('focusout', this.focusOutVolumeListener);
      }
    }
  }
}
